/**
 * Adapts the flat `PublicRosterRow[]` a public share-view payload carries
 * into the shape ConfirmedTab's columns read.
 *
 * A SIBLING of CreatorsTab's `publicRosterRowAdapter.ts`
 * (`components/CreatorsTab/publicRosterRowAdapter.ts`), not a reuse of it —
 * the two target shapes (`RosterEntryCreators` vs `RosterEntryFinalized`)
 * diverge on enough REQUIRED fields (this row's frozen `status:
 * 'accepted_by_creator'` literal, `posts`/`addons` production records vs.
 * Creators' `estimated_cpv`/`selection_thread_locked`/`unread_count`) that
 * forcing one generic adapter over both would need more branching than the
 * ~30 lines of straight field mapping each one actually needs — same
 * technique (adapt the flat wire row into a structurally-close authed shape,
 * cast at the one call site), applied twice rather than abstracted once.
 *
 * The public payload for the Confirmed tab is genuinely THINNER than
 * Creators' — see share_view_page.go's `statusesForShareTab` doc comment
 * (BE, `internal/campaigns/share_view_page.go`): a Confirmed-tab grant's rows
 * come from `buildRosterView` (entry-level `ListByRecency`), never
 * `buildProductionRosterView` (the roster_posts-backed path Scripts/Content/
 * Live-content use) — so there is NO posts[]/production-phase data behind a
 * public Confirmed row, ever, at ANY point on the BE's builder path today.
 * That's why this adapter always emits `posts: []` / `addons: []`, not a
 * placeholder that could theoretically be replaced with something more
 * complete later: the fields this row is missing (script/content status,
 * live_video, per-post CPV) don't have a public wire representation to draw
 * from in the first place.
 *
 * Downstream effect (documented once, here, rather than re-derived at every
 * call site that depends on it): `mainPost(c)` (ConfirmedTab.helpers.ts)
 * reads `c.posts[0]`, which is always `undefined` for an adapted row — so
 * `canViewScript`/`canViewContent` are always `false` and the row's Script /
 * Content buttons + live-video thumbnail render in their natural "not
 * available yet" disabled state with ZERO extra `isPublicView` branching in
 * ConfirmedTab.tsx's Actions column. The Status column's existing `post?.
 * status ?? c.status` fallback reaches `c.status` for the same reason — no
 * extra branching needed there either. Only the Deliverables / Total Cost /
 * CPV cells need an explicit `isPublicView` branch (see ConfirmedTab.tsx) —
 * because unlike posts/addons, `deliverables` genuinely IS present on the
 * public wire row (Confirmed rows have >= 1 selected deliverable, so
 * `buildPublicRosterRow`, BE, emits exactly the selected one(s)) and
 * `RosterEntryFinalized.deliverables` is a real field on the authed type too
 * — this adapter populates it with the real mapped values rather than a
 * placeholder.
 *
 * `id`/`user_id`/`platform`, `snapshot_metrics`, and `deliverables` are
 * mapped via `./publicRosterRowSharedFields.ts` — the overlap Task 17's
 * review found this adapter shares near-verbatim with Creators' sibling
 * (`CreatorsTab/publicRosterRowAdapter.ts`), extracted in Task 18 so a third
 * (and now fourth/fifth, Content/Live-Content) adapter doesn't re-copy it.
 * Only the fields genuinely specific to this tab are inlined below.
 */
import type { PublicRosterRow } from '@discovr/features';
import type { RosterEntryFinalized } from '@discovr/types';
import {
  adaptPublicRosterRowIdentity,
  adaptPublicRosterRowSnapshotMetrics,
  adaptPublicRosterRowDeliverables,
} from './publicRosterRowSharedFields';

/** Adapted row shape, structurally close to `RosterEntryFinalized` but with
 *  every production-phase field forced to its genuinely-absent-on-the-wire
 *  value — see this file's header for why `posts`/`addons` can never be
 *  anything but empty here. `estimated_reach` stays a placeholder `0`
 *  (ONLY to satisfy `RosterEntryFinalized`'s required-number field
 *  structurally — nothing reads it for Confirmed's own columns).
 *  `estimated_budget`/`estimated_cpv` both pass through the row's OWN
 *  negotiation-time figures (2026-08-05/2026-08-06 parity decisions) and ARE
 *  real here — see Total Cost's and Est. CPV's cells in ConfirmedTab.tsx.
 *  `estimated_cpv` is the SAME committed-at-acceptance figure the authed
 *  `RosterEntryFinalized.estimated_cpv` reads (main-post cost ÷ estimated
 *  views, frozen at acceptance) — not the actuals-based `post.cpv.live`,
 *  which this tab's public payload genuinely can't carry (no `posts[]` — see
 *  the file header). */
export interface PublicFinalizedRow {
  id: string;
  user_id: string;
  status: 'accepted_by_creator';
  status_updated_at: string;
  platform: {
    username: string;
    name: string;
    profile_picture_url: string;
    is_verified: boolean;
  };
  snapshot_metrics: {
    followers: number;
    avg_views: number;
    engagement_rate: number;
  };
  deliverables: Array<{ type: string; base_price?: number; is_selected: boolean }>;
  estimated_cpv: number | null;
  estimated_reach: number;
  estimated_budget: number;
  posts: never[];
  addons: never[];
  timestamps: { created_at: string; updated_at: string };
}

function adaptPublicRosterRow(row: PublicRosterRow): PublicFinalizedRow {
  return {
    ...adaptPublicRosterRowIdentity(row),
    status: 'accepted_by_creator',
    status_updated_at: '',
    ...adaptPublicRosterRowSnapshotMetrics(row),
    ...adaptPublicRosterRowDeliverables(row),
    // null (not 0) when the public payload carries no per-row CPV. EstimatedCPVCell
    // renders "—" for both null and a real 0 (never "₹0.00"), but null vs 0 stays
    // meaningful upstream, so a genuine 0 (if ever present) is preserved as-is.
    estimated_cpv: row.estimated_cpv ?? null,
    estimated_reach: 0,
    estimated_budget: row.estimated_budget ?? 0,
    posts: [],
    addons: [],
    timestamps: { created_at: '', updated_at: '' },
  };
}

export function adaptPublicRosterRows(rows: PublicRosterRow[]): PublicFinalizedRow[] {
  return rows.map(adaptPublicRosterRow);
}

/** Cast helper for the one call site (ConfirmedTab.tsx) that must feed these
 *  rows into `DataTable<RosterEntryFinalized>`. Kept as a named export
 *  (rather than an inline `as unknown as` at the call site) so the type gap
 *  is documented in exactly one place — mirrors
 *  `adaptPublicRosterRowsAsCreatorsRows` in CreatorsTab's sibling adapter. */
export function adaptPublicRosterRowsAsFinalizedRows(
  rows: PublicRosterRow[],
): RosterEntryFinalized[] {
  return adaptPublicRosterRows(rows) as unknown as RosterEntryFinalized[];
}
